import axios, { AxiosResponse } from 'axios'
import { cloneDeep, sortBy } from 'lodash'
import { PCP_SERVICE_LINE, PCP_SERVICE_LINE_NAMES } from '_helpers/constants'

import type { AxiosError, GetFacilities } from './types'
import { GetDoctors } from './types'

export const isResponseOk = <T>(
  res: AxiosResponse<T> | AxiosError
): res is AxiosResponse<T> => res.status === 200

const isGetDoctors = (res: GetDoctors | GetFacilities): res is GetDoctors =>
  Object.keys((res as GetDoctors).data).includes('providers') ||
  Object.keys((res as GetDoctors).data).includes('practitioners')

const isGetFacilities = (
  res: GetDoctors | GetFacilities
): res is GetFacilities => Array.isArray((res as GetFacilities).data)

export const isResultFulfiled = <T>(
  res: PromiseSettledResult<T>
): res is PromiseFulfilledResult<T> =>
  (res as PromiseFulfilledResult<T>).status === 'fulfilled'

export const flattenResults = <R extends GetDoctors | GetFacilities>(
  results: AxiosResponse<R>[]
) => {
  const searchResults = results
    .map(result => result.data)
    .filter(result => !!result)

  const result = cloneDeep(searchResults.shift() as R)

  searchResults.forEach(searchResult => {
    result.specialtyFilters = [
      ...result.specialtyFilters,
      ...(searchResult.specialtyFilters || []),
    ]
    result.networks = [...result.networks, ...(searchResult.networks || [])]
    result.count.total += searchResult.count.total

    if (isGetDoctors(searchResult) && isGetDoctors(result)) {
      result.count.unfilteredTotal += searchResult.count.unfilteredTotal
      result.count.unfilteredTotal += searchResult.count.unfilteredTotal
      result.count.male += searchResult.count.male
      result.count.female += searchResult.count.female
      result.count.genderNotInformed += searchResult.count.genderNotInformed
      result.count.acceptingNewPatients +=
        searchResult.count.acceptingNewPatients
      result.count.virtualCare.only += searchResult.count.virtualCare.only
      result.count.virtualCare.available +=
        searchResult.count.virtualCare.available
      result.count.virtualCare.unavailable +=
        searchResult.count.virtualCare.unavailable
      result.data.providers = sortBy(
        [...result.data.providers, ...(searchResult.data.providers || [])],
        ['distance']
      )
      result.data.practitioners = sortBy(
        [
          ...result.data.practitioners,
          ...(searchResult.data.practitioners || []),
        ],
        ['distance']
      )
    }

    if (isGetFacilities(searchResult) && isGetFacilities(result)) {
      result.data = sortBy(
        [...result.data, ...(searchResult.data || [])],
        ({ distance }) => Number(distance)
      )
    }
  })

  // result.specialtyFilters = sortBy(
  //   unique(result.specialtyFilters, ['name', 'code']),
  //   ['name']
  // )

  return result
}

export const isSuccessResponse = <T = any>(
  response: AxiosResponse<T> | AxiosResponse<any>
): response is AxiosResponse<T> => {
  return (response as AxiosResponse<T>).status.toString().startsWith('20')
}

export async function parseAxiosErrors<
  F extends () => Promise<AxiosResponse<any>>,
  T = any
>(func: F): Promise<ReturnType<F> | AxiosResponse<any>> {
  try {
    const response = await func()

    return response
  } catch (e: any) {
    console.error(e)

    if (axios.isAxiosError(e)) {
      return e.response as AxiosResponse<T>
    }

    throw new Error(e)
  }
}

export const getIsPcpValue = (serviceLines?: string) =>
  PCP_SERVICE_LINE_NAMES.includes(serviceLines ?? '')

export const getPractitionersServiceLinesValue = (serviceLines?: string) =>
  PCP_SERVICE_LINE === serviceLines ? undefined : serviceLines
